/* eslint-disable no-console */
import { captureException } from '@sentry/nextjs'

import { isBrowser, isLocal, isProduction } from '../../../lib/helpers'

interface LogFn {
  (message?: any, ...optionalParams: any[]): void
}

type LogLevel = 'debug' | 'log' | 'warn' | 'error' | 'silent'

const logLevels = new Set(['debug', 'log', 'warn', 'error', 'silent'])

interface ILogger {
  log: LogFn
  warn: LogFn
  error: LogFn
  debug: LogFn

  logLevel: LogLevel
  changeLogLevel: (level: LogLevel) => void

  trackError: (error: Error) => void
}

declare global {
  interface Window {
    IgnLogger: ILogger
  }
}

const NO_OP: LogFn = (_message?: unknown, ..._optionalParams: any[]) => undefined

export class Logger implements ILogger {
  private static instance: Logger

  static getInstance() {
    if (this.instance) {
      return this.instance
    }
    this.instance = new Logger()
    if (isBrowser()) {
      window.IgnLogger = this.instance
    }
    return this.instance
  }

  static formatMessage(message: unknown) {
    const cache = []
    if (typeof message === 'object' && message !== null) {
      return JSON.stringify(
        message,
        (key, value) => {
          if (typeof value === 'object' && value !== null) {
            if (cache.includes(value)) {
              // Circular reference found, discard key
              return undefined
            }
            // Store value in our collection
            cache.push(value)
          }
          return value
        },
        2
      )
    }
    return message
  }

  private displayAlertOnError = isLocal()

  logLevel: LogLevel

  log: LogFn

  warn: LogFn

  error: LogFn

  debug: LogFn

  constructor(options?: { level?: LogLevel }) {
    const { level } = options || {}
    this.logLevel =
      level ||
      (logLevels.has(process.env.NEXT_PUBLIC_LOG_LEVEL) && (process.env.NEXT_PUBLIC_LOG_LEVEL as LogLevel)) ||
      (isProduction() ? 'error' : isLocal() ? 'debug' : 'log')

    this.changeLogLevel(this.logLevel)
  }

  changeLogLevel(level: LogLevel) {
    this.error = NO_OP
    this.warn = NO_OP
    this.log = NO_OP
    this.debug = NO_OP

    if (level === 'silent') {
      return
    }

    this.error = (message, ...params) => console.error.call(console, Logger.formatMessage(message), ...params)

    if (level === 'error') {
      return
    }

    this.warn = (message, ...params) => console.warn.call(console, Logger.formatMessage(message), ...params)

    if (level === 'warn') {
      return
    }

    this.log = (message, ...params) => console.log.call(console, Logger.formatMessage(message), ...params)

    if (level === 'log') {
      return
    }

    this.debug = (message, ...params) => console.debug.call(console, Logger.formatMessage(message), ...params)
  }

  trackError(error: Error) {
    this.error(error)

    if (!isLocal()) {
      // Send error to Sentry
      captureException(error)
    } else if (isBrowser() && this.displayAlertOnError) {
      // Display alert on error once in 5 sec in local development
      this.displayAlertOnError = false
      // eslint-disable-next-line no-alert
      alert(error.message)

      setTimeout(() => {
        this.displayAlertOnError = true
      }, 5 * 1000)
    }
  }
}

export default Logger
